import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
  EmptyState,
  FormCheckbox,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell
} from '../components/common';

const UserRoles = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchUserDetails();
    fetchAvailableRoles();
    fetchUserRoles();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      const response = await api.get(`/Users/${userId}`);
      setUser(response.data);
    } catch (err) {
      console.error('Failed to fetch user details:', err);
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const response = await api.get('/Users/roles');
      setAvailableRoles(response.data || []);
    } catch (err) {
      console.error('Failed to fetch available roles:', err);
      setAvailableRoles([]);
    }
  };

  const fetchUserRoles = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/Users/${userId}/roles`);
      const roles = response.data || [];
      setUserRoles(roles);
      setSelectedRoles(roles);
    } catch (err) {
      setError(err.message || 'Failed to fetch user roles');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (role) => {
    setSelectedRoles(prev => {
      if (prev.includes(role)) {
        return prev.filter(r => r !== role);
      } else {
        return [...prev, role];
      }
    });
    // Clear any existing success message when making changes
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await api.put(`/Users/${userId}/roles`, selectedRoles);
      setUserRoles(selectedRoles);
      setSuccess('User roles updated successfully');
      // Navigate back after showing success message
      setTimeout(() => navigate(`/users/${userId}`), 2000);
    } catch (err) {
      setError(err.message || 'Failed to update user roles');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (userRoles.length !== selectedRoles.length) return true;
    return !userRoles.every(role => selectedRoles.includes(role));
  };

  const getRoleDescription = (role) => {
    const descriptions = {
      'Admin': 'Full system administration access',
      'Manager': 'Building management capabilities',
      'Owner': 'Property owner privileges',
      'Resident': 'Resident access to building features',
      'Staff': 'Building staff member access',
      'SuperAdmin': 'Super administrator with all permissions',
      'Support': 'Customer support access'
    };
    return descriptions[role] || 'Standard user role';
  };

  const getRoleVariant = (role) => {
    const variants = {
      'Admin': 'danger',
      'SuperAdmin': 'secondary',
      'Manager': 'primary',
      'Owner': 'accent',
      'Resident': 'secondary',
      'Staff': 'outline',
      'Support': 'secondary'
    };
    return variants[role] || 'secondary';
  };

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Manage Roles for ${user?.firstName} ${user?.lastName}`}
        subtitle={user?.email}
        backLink={{ label: "Back to User Details", href: `/users/${userId}` }}
      />

      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      {success && (
        <SuccessMessage
          message={success}
          onDismiss={() => setSuccess(null)}
        />
      )}

      <Card>
        <CardHeader
          title="Available Roles"
          subtitle="Select the roles to assign to this user"
        />
        <CardBody>
          {availableRoles.length === 0 ? (
            <EmptyState
              message="No roles available"
              icon="🔒"
            />
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell header className="w-12 text-center"></TableCell>
                    <TableCell header>Role</TableCell>
                    <TableCell header>Description</TableCell>
                    <TableCell header>Status</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {availableRoles.map((role) => {
                    const isSelected = selectedRoles.includes(role);
                    const isAssigned = userRoles.includes(role);

                    return (
                      <TableRow
                        key={role}
                        clickable
                        onClick={() => handleRoleToggle(role)}
                        className={isSelected ? 'bg-primary-50/50' : ''}
                      >
                        <TableCell>
                          <div className="flex justify-center">
                            <FormCheckbox
                              checked={isSelected}
                              onChange={() => handleRoleToggle(role)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-slate-900">{role}</span>
                            <Badge variant={getRoleVariant(role)} size="small">
                              {role}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">
                            {getRoleDescription(role)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {isAssigned ? (
                            <Badge variant="success" size="small">Currently assigned</Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between pt-5 border-t border-slate-100">
            <div className="text-sm font-medium text-slate-600">
              {selectedRoles.length} role{selectedRoles.length !== 1 ? 's' : ''} selected
            </div>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={() => navigate(`/users/${userId}`)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={!hasChanges()}
                loading={saving}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {hasChanges() && (
        <Card>
          <CardBody>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> You have unsaved changes. Click "Save Changes" to apply the new role assignments.
              </p>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default UserRoles;